// Iter 200 — Live AI · Next Goal page
//
// Aggregates the AI engine output for every in-play match:
//   - Goal Heat Meter (per side)
//   - Momentum Timeline (rolling LMI graph)
//   - Pressure Timeline (rolling PDS graph)
//   - Expected Next-Goal Clock (countdown ETA)
//   - Goal Probability Graph (sparkline of P(home/away/none))
//   - Team Dominance Graph (bar)
//   - Live Alerts feed (when probability > 70%)
//   - Automatic confidence recalibration display
//
// Adaptive polling: 5s on the page, the backend itself dispatches every
// 15s or 30s based on the alert flag.
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { TopNav } from "../components/TopNav";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Activity, Flame, RefreshCw, BellRing, ChevronRight, Clock, Gauge,
  TrendingUp, ShieldAlert, Zap, ArrowLeft, Trophy, BarChart3, Target,
  Sparkles, Globe, ChevronDown,
} from "lucide-react";
import { API } from "../App";
import { formatLiveMinute } from "../lib/liveMinute";
import GoldFinlandPanel from "../components/GoldFinlandPanel";

// ───────────────────────────────────────────────────────────────────────
// Small visual primitives
// ───────────────────────────────────────────────────────────────────────

const HeatMeter = ({ label, value, accent = "rose" }) => {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const palette = {
    rose: "from-rose-500 to-orange-400",
    sky: "from-sky-500 to-cyan-400",
  }[accent] || "from-rose-500 to-orange-400";
  return (
    <div data-testid={`heat-meter-${accent}`} className="space-y-1">
      <div className="flex justify-between text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        <span>{label}</span>
        <span className="tabular-nums font-bold text-foreground">{pct.toFixed(0)}</span>
      </div>
      <div className="relative h-2.5 rounded-full bg-muted overflow-hidden">
        <div
          className={`absolute inset-y-0 left-0 rounded-full bg-gradient-to-r ${palette} shadow-[0_0_18px_rgba(244,63,94,0.45)]`}
          style={{ width: `${pct}%`, transition: "width 600ms cubic-bezier(.4,.0,.2,1)" }}
        />
      </div>
    </div>
  );
};

const ProbabilityBar = ({ home, away, none }) => {
  const total = home + away + none || 1;
  const hPct = (home / total) * 100;
  const aPct = (away / total) * 100;
  const nPct = (none / total) * 100;
  return (
    <div data-testid="probability-bar" className="space-y-1.5">
      <div className="flex h-3.5 rounded-full overflow-hidden border border-border">
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-400" style={{ width: `${hPct}%`, transition: "width 600ms ease-out" }} />
        <div className="bg-gradient-to-r from-sky-500 to-sky-400" style={{ width: `${aPct}%`, transition: "width 600ms ease-out" }} />
        <div className="bg-gradient-to-r from-zinc-500 to-zinc-400" style={{ width: `${nPct}%`, transition: "width 600ms ease-out" }} />
      </div>
      <div className="flex justify-between text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Home {home.toFixed(1)}%</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-zinc-500" /> No goal {none.toFixed(1)}%</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-sky-500" /> Away {away.toFixed(1)}%</span>
      </div>
    </div>
  );
};

const Sparkline = ({ points = [], color = "#10b981", label }) => {
  // points: array of { x, y } where y is 0..100
  if (!points.length) {
    return (
      <div className="h-14 flex items-center justify-center text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        gathering data…
      </div>
    );
  }
  const w = 320, h = 60, pad = 4;
  const xMin = Math.min(...points.map(p => p.x));
  const xMax = Math.max(...points.map(p => p.x));
  const xSpan = Math.max(xMax - xMin, 1);
  const toX = (x) => pad + ((x - xMin) / xSpan) * (w - pad * 2);
  const toY = (y) => h - pad - (y / 100) * (h - pad * 2);
  const d = points.map((p, i) => `${i === 0 ? "M" : "L"} ${toX(p.x).toFixed(1)} ${toY(p.y).toFixed(1)}`).join(" ");
  const last = points[points.length - 1];
  return (
    <div className="space-y-1">
      {label && (
        <div className="flex justify-between text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          <span>{label}</span>
          <span className="tabular-nums font-bold text-foreground">{last.y.toFixed(0)}</span>
        </div>
      )}
      <svg width="100%" viewBox={`0 0 ${w} ${h}`} className="overflow-visible">
        <defs>
          <linearGradient id={`grad-${label}-${color.replace("#", "")}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.45" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d={`${d} L ${toX(last.x).toFixed(1)} ${h - pad} L ${toX(points[0].x).toFixed(1)} ${h - pad} Z`}
          fill={`url(#grad-${label}-${color.replace("#", "")})`}
        />
        <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={toX(last.x)} cy={toY(last.y)} r="3" fill={color} stroke="hsl(var(--background))" strokeWidth="1.5" />
      </svg>
    </div>
  );
};

const ConfidenceBadge = ({ grade, score }) => {
  const palette = {
    "A+": "bg-emerald-500/15 text-emerald-500 border-emerald-500/40",
    "A":  "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    "B":  "bg-amber-500/10 text-amber-500 border-amber-500/30",
    "C":  "bg-rose-500/10 text-rose-500 border-rose-500/30",
  }[grade] || "bg-muted text-muted-foreground border-border";
  return (
    <Badge data-testid="confidence-badge" className={`gap-1.5 px-2 py-1 border ${palette}`}>
      <Zap className="w-3 h-3" />
      <span className="font-bold tracking-wider">{grade}</span>
      <span className="opacity-70 tabular-nums">{score?.toFixed?.(0)}</span>
    </Badge>
  );
};

const RiskTag = ({ risk }) => {
  const palette = {
    "Low": "bg-emerald-500/10 text-emerald-500 border-emerald-500/30",
    "Medium": "bg-amber-500/10 text-amber-500 border-amber-500/30",
    "High": "bg-rose-500/10 text-rose-500 border-rose-500/30",
  }[risk] || "bg-muted text-muted-foreground border-border";
  return (
    <Badge data-testid="risk-tag" className={`border ${palette} uppercase tracking-[0.16em] text-[10px]`}>
      {risk} risk
    </Badge>
  );
};

// ───────────────────────────────────────────────────────────────────────
// Match card (list view)
// ───────────────────────────────────────────────────────────────────────

const MatchCard = ({ match }) => {
  const pred = match.prediction || {};
  const probs = pred.probabilities || {};
  const rec = pred.recommendation || {};
  return (
    <Link
      to={`/live-ai/${match.fixture_id}`}
      data-testid={`match-card-${match.fixture_id}`}
      className="block group"
    >
      <Card className="relative overflow-hidden border-border hover:border-primary/60 transition-all hover:shadow-[0_0_36px_rgba(244,63,94,0.18)]">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-rose-500/40 to-transparent" />
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="relative flex w-1.5 h-1.5">
                <span className="absolute inline-flex w-full h-full rounded-full bg-rose-500 opacity-75 animate-ping" />
                <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-rose-500" />
              </span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-rose-500 font-bold">
                {formatLiveMinute(match.minute, match.minute_extra, match.status) || match.status}
              </span>
            </div>
            <ConfidenceBadge grade={rec.confidence_grade} score={pred.confidence?.score} />
          </div>

          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
            <div className="text-right">
              <div className="text-sm font-bold truncate">{match.home_team}</div>
              <div className="text-[10px] text-muted-foreground">Home</div>
            </div>
            <div className="text-xl font-black tabular-nums px-2">
              {match.score?.home} <span className="text-muted-foreground">–</span> {match.score?.away}
            </div>
            <div>
              <div className="text-sm font-bold truncate">{match.away_team}</div>
              <div className="text-[10px] text-muted-foreground">Away</div>
            </div>
          </div>

          <ProbabilityBar
            home={probs.home_next_goal_pct || 0}
            away={probs.away_next_goal_pct || 0}
            none={probs.no_goal_15min_pct || 0}
          />

          <div className="flex items-center justify-between pt-1">
            <div className="text-[11px] text-muted-foreground">
              <span className="font-semibold text-foreground">{rec.primary_pick?.label}</span>
              {" · "}
              <span className="tabular-nums">{rec.primary_pick?.probability?.toFixed?.(1)}%</span>
            </div>
            <div className="flex items-center gap-2">
              <RiskTag risk={rec.risk} />
              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-rose-500 transition" />
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

// ───────────────────────────────────────────────────────────────────────
// Detail view
// ───────────────────────────────────────────────────────────────────────

const DetailView = ({ fixtureId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [err, setErr] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [a, b] = await Promise.all([
          axios.get(`${API}/live/next-goal/${fixtureId}`),
          axios.get(`${API}/live/next-goal/${fixtureId}/timeline`),
        ]);
        if (cancelled) return;
        setData(a.data);
        setTimeline(b.data?.points || []);
        setErr(null);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Live data not available yet.");
      }
    };
    load();
    pollRef.current = setInterval(load, 5000);
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fixtureId]);

  // Build sparkline points
  const probHomePts = useMemo(
    () => timeline.map((p, i) => ({ x: p.minute ?? i, y: p.probabilities?.home_next_goal_pct ?? 0 })),
    [timeline]
  );
  const probAwayPts = useMemo(
    () => timeline.map((p, i) => ({ x: p.minute ?? i, y: p.probabilities?.away_next_goal_pct ?? 0 })),
    [timeline]
  );
  const lmiHomePts = useMemo(
    () => timeline.map((p, i) => ({ x: p.minute ?? i, y: (p.lmi?.home ?? 0) * 100 })),
    [timeline]
  );
  const lmiAwayPts = useMemo(
    () => timeline.map((p, i) => ({ x: p.minute ?? i, y: (p.lmi?.away ?? 0) * 100 })),
    [timeline]
  );
  const pdsHomePts = useMemo(
    () => timeline.map((p, i) => ({ x: p.minute ?? i, y: Math.min(100, (p.pds?.home ?? 0) * 25) })),
    [timeline]
  );
  const pdsAwayPts = useMemo(
    () => timeline.map((p, i) => ({ x: p.minute ?? i, y: Math.min(100, (p.pds?.away ?? 0) * 25) })),
    [timeline]
  );

  if (err && !data) {
    return (
      <div className="text-center text-muted-foreground py-12" data-testid="live-detail-empty">
        <Activity className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p className="font-semibold text-foreground mb-1">Live stats not available for this match</p>
        <p className="text-sm max-w-md mx-auto">
          API-Sports doesn&apos;t provide live momentum / xG / shot data for this league
          (typically USL League Two, NCAA, friendlies, or lower-tier women&apos;s
          divisions). The AI engine only surfaces predictions where real data is
          flowing — no fake 50/50 guesses.
        </p>
        <Button variant="outline" className="mt-5" onClick={() => navigate("/live-ai")} data-testid="back-to-list-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to live AI
        </Button>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="text-center text-muted-foreground py-12">
        <Activity className="w-10 h-10 mx-auto mb-3 animate-pulse opacity-40" />
        <p>Loading live AI engine…</p>
      </div>
    );
  }

  const pred = data.prediction || {};
  const probs = pred.probabilities || {};
  const rec = pred.recommendation || {};
  const conf = pred.confidence || {};
  const dom = pred.dominance || {};
  const heat = pred.goal_heat || {};
  const lmi = pred.lmi || {};
  const pds = pred.pds || {};
  const xgm = pred.xgm || {};
  const tw = pred.time_weights || {};
  const ftPred = pred.ft_score_prediction;
  const anyMore = ftPred?.any_more_goals;
  const breakdown = pred.next_goal_breakdown;

  return (
    <div className="space-y-6" data-testid="live-detail">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate("/live-ai")} data-testid="back-to-list-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> All live AI matches
        </Button>
        <ConfidenceBadge grade={rec.confidence_grade} score={conf.score} />
      </div>

      {/* Scoreboard + recommendation hero */}
      <Card className="overflow-hidden border-rose-500/30">
        <div className="px-5 py-4 bg-gradient-to-br from-rose-500/10 via-transparent to-sky-500/10 border-b border-border">
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
            <div className="text-right">
              <div className="text-base sm:text-lg font-bold truncate">{data.home_team}</div>
            </div>
            <div className="text-2xl sm:text-3xl font-black tabular-nums">
              {data.score?.home} <span className="text-muted-foreground">–</span> {data.score?.away}
            </div>
            <div>
              <div className="text-base sm:text-lg font-bold truncate">{data.away_team}</div>
            </div>
          </div>
          <div className="text-center mt-2 text-[11px] uppercase tracking-[0.2em] text-rose-500 font-bold">
            {data.status === "HT" ? "Half time" : (formatLiveMinute(data.minute, data.minute_extra, data.status) || data.status)}
            {data.data_source && (
              <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted text-muted-foreground text-[9px] tracking-[0.16em] border border-border">
                {data.data_source === "betsapi" ? "BetsAPI" : "API-Sports"}
              </span>
            )}
          </div>
        </div>

        <CardContent className="p-5 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Primary AI pick</div>
              <div className="text-lg sm:text-xl font-bold mt-1">{rec.primary_pick?.label}</div>
              <div className="text-2xl font-black text-rose-500 tabular-nums mt-1">
                {rec.primary_pick?.probability?.toFixed?.(1)}%
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <RiskTag risk={rec.risk} />
              <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" /> Next goal ETA · {pred.expected_next_goal_eta_min}&apos;
              </div>
            </div>
          </div>

          {rec.reason && (
            <p className="text-sm text-muted-foreground border-l-2 border-rose-500/40 pl-3 italic">
              {rec.reason}
            </p>
          )}

          <ProbabilityBar
            home={probs.home_next_goal_pct || 0}
            away={probs.away_next_goal_pct || 0}
            none={probs.no_goal_15min_pct || 0}
          />

          {rec.backup_pick && (
            <div className="text-[11px] text-muted-foreground pt-1">
              Backup: <span className="font-semibold text-foreground">{rec.backup_pick.label}</span>
              {" · "}
              <span className="tabular-nums">{rec.backup_pick.probability?.toFixed?.(1)}%</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Full-time score projection — only visible in the last 15 minutes */}
      {ftPred && (
        <Card className="border-amber-500/40 bg-gradient-to-br from-amber-500/5 via-transparent to-rose-500/5" data-testid="ft-score-card">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-500" />
                <span className="text-xs uppercase tracking-[0.18em] font-bold">Full-time score projection</span>
              </div>
              <Badge className="bg-amber-500/15 text-amber-500 border border-amber-500/40 uppercase tracking-[0.16em] text-[10px]">
                {ftPred.minutes_remaining > 15 ? `~${ftPred.minutes_remaining} min left` : `Last ${ftPred.minutes_remaining} min`}
              </Badge>
            </div>

            <div className="flex items-end gap-4 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Most likely FT</div>
                <div className="text-4xl font-black tabular-nums tracking-tight mt-1" data-testid="ft-most-likely">
                  {ftPred.most_likely}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Probability: <span className="font-bold text-amber-500 tabular-nums">{ftPred.most_likely_pct?.toFixed?.(1)}%</span>
                </div>
              </div>

              <div className="flex-1 min-w-[200px] space-y-1">
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">Alternative outcomes</div>
                {(ftPred.alternatives || []).slice(0, 4).map((alt) => (
                  <div key={alt.score} className="flex items-center justify-between gap-2 text-xs">
                    <span className="tabular-nums font-semibold">{alt.score}</span>
                    <div className="flex-1 mx-2 h-1.5 bg-muted rounded overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-amber-500 to-rose-400"
                        style={{ width: `${Math.min(100, alt.probability * 2.5)}%`, transition: "width 600ms ease-out" }}
                      />
                    </div>
                    <span className="tabular-nums text-muted-foreground w-10 text-right">{alt.probability?.toFixed?.(1)}%</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 pt-3 border-t border-border">
              <div className="text-center">
                <div className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">{data.home_team} win</div>
                <div className="text-sm font-bold text-emerald-500 tabular-nums">
                  {ftPred.outcome_probabilities?.home_win_pct?.toFixed?.(1)}%
                </div>
              </div>
              <div className="text-center">
                <div className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">Draw</div>
                <div className="text-sm font-bold text-zinc-400 tabular-nums">
                  {ftPred.outcome_probabilities?.draw_pct?.toFixed?.(1)}%
                </div>
              </div>
              <div className="text-center">
                <div className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">{data.away_team} win</div>
                <div className="text-sm font-bold text-sky-500 tabular-nums">
                  {ftPred.outcome_probabilities?.away_win_pct?.toFixed?.(1)}%
                </div>
              </div>
            </div>

            <div className="text-[10px] text-muted-foreground">
              Expected remaining goals: <span className="font-semibold text-foreground tabular-nums">{ftPred.lambda_home_remaining?.toFixed?.(2)}</span> home /
              <span className="font-semibold text-foreground tabular-nums"> {ftPred.lambda_away_remaining?.toFixed?.(2)}</span> away
              {" · "}method: {ftPred.method?.replace("_", " ")}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Any more goals in remaining time? */}
      {anyMore && (
        <Card className="border-emerald-500/30" data-testid="any-more-goals-card">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-emerald-500" />
                <span className="text-xs uppercase tracking-[0.18em] font-bold">
                  Any more goals in remaining {ftPred?.minutes_remaining} min?
                </span>
              </div>
              <Badge className={`border uppercase tracking-[0.16em] text-[10px] ${
                anyMore.verdict === "more goals likely"
                  ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/40"
                  : anyMore.verdict === "drought likely"
                  ? "bg-zinc-500/10 text-zinc-400 border-zinc-500/40"
                  : "bg-amber-500/10 text-amber-500 border-amber-500/40"
              }`}>
                {anyMore.verdict}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">≥ 1 more goal</div>
                <div className="text-2xl font-black text-emerald-500 tabular-nums" data-testid="more-goals-yes">
                  {anyMore.p_at_least_one_more_pct?.toFixed?.(1)}%
                </div>
              </div>
              <div className="p-3 rounded-lg bg-muted/40 border border-border">
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">No more goals</div>
                <div className="text-2xl font-black text-zinc-400 tabular-nums" data-testid="more-goals-no">
                  {anyMore.p_no_more_goals_pct?.toFixed?.(1)}%
                </div>
              </div>
            </div>
            <div className="text-[10px] text-muted-foreground">
              Expected remaining goals from both teams combined:
              {" "}<span className="font-bold text-foreground tabular-nums">{anyMore.expected_remaining_goals?.toFixed?.(2)}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Parameter-by-parameter breakdown */}
      {breakdown && breakdown.parameters && (
        <Card data-testid="next-goal-breakdown-card">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-sky-500" />
                <span className="text-xs uppercase tracking-[0.18em] font-bold">
                  Parameters considered ({breakdown.summary?.total_parameters})
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] flex-wrap">
                <span className="text-emerald-500 font-bold">{data.home_team}</span>
                <span className="text-foreground tabular-nums">{breakdown.summary?.home_advantage_count}</span>
                <span className="text-muted-foreground">·</span>
                <span className="text-zinc-400 font-bold">balanced</span>
                <span className="text-foreground tabular-nums">{breakdown.summary?.balanced_count}</span>
                <span className="text-muted-foreground">·</span>
                <span className="text-sky-500 font-bold">{data.away_team}</span>
                <span className="text-foreground tabular-nums">{breakdown.summary?.away_advantage_count}</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              {Object.entries(breakdown.parameters).map(([key, p]) => {
                const advClass = p.advantage === "home"
                  ? "border-emerald-500/30 bg-emerald-500/5"
                  : p.advantage === "away"
                  ? "border-sky-500/30 bg-sky-500/5"
                  : "border-border bg-muted/20";
                const arrow = p.advantage === "home" ? "←" : p.advantage === "away" ? "→" : "·";
                const valFormat = (v) => {
                  if (typeof v !== "number") return "—";
                  if (Number.isInteger(v)) return v.toString();
                  return v.toFixed(2);
                };
                return (
                  <div
                    key={key}
                    className={`px-3 py-2 rounded-lg border ${advClass} transition`}
                    data-testid={`param-${key}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground truncate">
                        {p.label}
                      </div>
                      {p.weight_pct > 0 && (
                        <div className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
                          weight {p.weight_pct}%
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-1">
                      <div className={`text-sm font-bold tabular-nums ${
                        p.advantage === "home" ? "text-emerald-500" : "text-foreground"
                      }`}>
                        {valFormat(p.home_value)}
                      </div>
                      <div className="text-xs text-muted-foreground tabular-nums">{arrow}</div>
                      <div className={`text-sm font-bold tabular-nums ${
                        p.advantage === "away" ? "text-sky-500" : "text-foreground"
                      }`}>
                        {valFormat(p.away_value)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="text-[10px] text-muted-foreground pt-2 border-t border-border">
              <Sparkles className="inline w-3 h-3 mr-1" />
              The next-goal probabilities at the top of this page are the *ensemble* output of all of these signals,
              weighted by time-of-match.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Heat + Dominance */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Flame className="w-4 h-4 text-rose-500" />
              <span className="text-xs uppercase tracking-[0.18em] font-bold">Goal heat meter</span>
            </div>
            <HeatMeter label={data.home_team} value={heat.home} accent="rose" />
            <HeatMeter label={data.away_team} value={heat.away} accent="sky" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Gauge className="w-4 h-4 text-sky-500" />
              <span className="text-xs uppercase tracking-[0.18em] font-bold">Team dominance</span>
            </div>
            <HeatMeter label={`${data.home_team} · ${dom.home?.toFixed?.(0)}/100`} value={dom.home} accent="rose" />
            <HeatMeter label={`${data.away_team} · ${dom.away?.toFixed?.(0)}/100`} value={dom.away} accent="sky" />
          </CardContent>
        </Card>
      </div>

      {/* Sparklines: probability, momentum, pressure */}
      <Card>
        <CardContent className="p-5 space-y-5">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-emerald-500" />
              <span className="text-xs uppercase tracking-[0.18em] font-bold">Goal probability graph</span>
            </div>
            <Sparkline points={probHomePts} color="#10b981" label={`${data.home_team} P(next goal) %`} />
            <Sparkline points={probAwayPts} color="#0ea5e9" label={`${data.away_team} P(next goal) %`} />
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-amber-500" />
              <span className="text-xs uppercase tracking-[0.18em] font-bold">Momentum timeline (LMI)</span>
            </div>
            <Sparkline points={lmiHomePts} color="#f59e0b" label={`${data.home_team} LMI`} />
            <Sparkline points={lmiAwayPts} color="#a855f7" label={`${data.away_team} LMI`} />
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <ShieldAlert className="w-4 h-4 text-rose-500" />
              <span className="text-xs uppercase tracking-[0.18em] font-bold">Pressure timeline (PDS)</span>
            </div>
            <Sparkline points={pdsHomePts} color="#f43f5e" label={`${data.home_team} pressure`} />
            <Sparkline points={pdsAwayPts} color="#22d3ee" label={`${data.away_team} pressure`} />
          </div>
        </CardContent>
      </Card>

      {/* Time-aware weights + confidence components + xG snapshot */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-amber-500" />
              <span className="text-xs uppercase tracking-[0.18em] font-bold">Time-aware feature weights</span>
            </div>
            <div className="space-y-2 text-xs">
              {Object.entries(tw).map(([k, v]) => (
                <div key={k} className="space-y-1">
                  <div className="flex justify-between">
                    <span className="capitalize text-muted-foreground">{k}</span>
                    <span className="tabular-nums font-semibold">{(v * 100).toFixed(0)}%</span>
                  </div>
                  <div className="h-1.5 rounded bg-muted overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-amber-500 to-orange-400" style={{ width: `${v * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-4 h-4 text-emerald-500" />
              <span className="text-xs uppercase tracking-[0.18em] font-bold">Auto-recalibrated confidence</span>
            </div>
            <div className="space-y-2 text-xs">
              {Object.entries(conf.components || {}).map(([k, v]) => (
                <div key={k} className="space-y-1">
                  <div className="flex justify-between">
                    <span className="capitalize text-muted-foreground">{k.replaceAll("_", " ")}</span>
                    <span className="tabular-nums font-semibold">{Number(v).toFixed(1)}</span>
                  </div>
                  <div className="h-1.5 rounded bg-muted overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-emerald-500 to-sky-400" style={{ width: `${Math.min(100, Number(v))}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2 pt-3 border-t border-border">
              <div className="text-center">
                <div className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">xGM H</div>
                <div className="text-sm font-bold tabular-nums">{xgm.home?.toFixed?.(2)}</div>
              </div>
              <div className="text-center">
                <div className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">LMI ∆</div>
                <div className="text-sm font-bold tabular-nums">
                  {((lmi.home || 0) - (lmi.away || 0)).toFixed(2)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">xGM A</div>
                <div className="text-sm font-bold tabular-nums">{xgm.away?.toFixed?.(2)}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="text-[10px] text-muted-foreground text-center pt-2">
        Engine {pred.engine_version} · auto-refresh every 5s · adaptive backend cadence 15/30s
      </div>
    </div>
  );
};

// ───────────────────────────────────────────────────────────────────────
// Iter 275 — All live games grouped by league (next-goal for every game)
// ───────────────────────────────────────────────────────────────────────

const MiniProbBar = ({ home, away, none }) => {
  const total = (home || 0) + (away || 0) + (none || 0) || 1;
  return (
    <div className="flex h-1.5 rounded-full overflow-hidden bg-muted w-full">
      <div className="bg-emerald-500" style={{ width: `${((home || 0) / total) * 100}%` }} />
      <div className="bg-sky-500" style={{ width: `${((away || 0) / total) * 100}%` }} />
      <div className="bg-zinc-500" style={{ width: `${((none || 0) / total) * 100}%` }} />
    </div>
  );
};

const AllLiveRow = ({ m }) => {
  const probs = m.prediction?.probabilities || {};
  const pick = m.prediction?.primary_pick || {};
  const body = (
    <div
      className={`grid grid-cols-[52px_1fr_auto] sm:grid-cols-[52px_1fr_220px_auto] items-center gap-3 px-3 py-2.5 border-b border-border/60 last:border-b-0 transition-colors ${m.has_detail ? "hover:bg-rose-500/[0.05] cursor-pointer" : ""}`}
      data-testid={`all-live-row-${m.fixture_id}`}
    >
      <div className="flex items-center gap-1.5">
        <span className="relative flex w-1.5 h-1.5 shrink-0">
          <span className="absolute inline-flex w-full h-full rounded-full bg-rose-500 opacity-75 animate-ping" />
          <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-rose-500" />
        </span>
        <span className="text-[10px] font-bold text-rose-500 tabular-nums uppercase">
          {formatLiveMinute(m.minute, m.minute_extra, m.status) || m.status}
        </span>
      </div>
      <div className="min-w-0">
        <div className="text-xs sm:text-sm font-semibold truncate">
          {m.home_team} <span className="text-muted-foreground font-black tabular-nums mx-1">{m.score?.home}–{m.score?.away}</span> {m.away_team}
        </div>
        <div className="mt-1.5 max-w-[260px]"><MiniProbBar home={probs.home_next_goal_pct} away={probs.away_next_goal_pct} none={probs.no_goal_15min_pct} /></div>
      </div>
      <div className="hidden sm:block text-right min-w-0">
        <div className="text-[11px] font-bold truncate">{pick.label}</div>
        <div className="text-xs font-black text-rose-500 tabular-nums">{pick.probability?.toFixed?.(1)}%</div>
      </div>
      <div className="flex items-center gap-2 justify-end">
        <Badge
          className={`border text-[8px] uppercase tracking-[0.14em] px-1.5 py-0.5 ${m.coverage === "full"
            ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/30"
            : "bg-zinc-500/10 text-zinc-400 border-zinc-500/25"}`}
        >
          {m.coverage === "full" ? "AI full" : "Model"}
        </Badge>
        {m.has_detail && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
      </div>
    </div>
  );
  return m.has_detail ? <Link to={`/live-ai/${m.fixture_id}`} className="block">{body}</Link> : body;
};

const AllLiveByLeague = ({ data }) => {
  const [collapsed, setCollapsed] = useState({});
  if (!data) return null;
  const leagues = data.leagues || [];
  return (
    <section className="space-y-3" data-testid="all-live-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-rose-500" />
          <h2 className="text-sm font-black uppercase tracking-[0.18em]">All live games · by league</h2>
          <Badge className="bg-rose-500/10 text-rose-500 border border-rose-500/30 tabular-nums" data-testid="all-live-count">
            {data.count} live
          </Badge>
        </div>
        <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
          {data.full_coverage} full AI · {Math.max(0, (data.count || 0) - (data.full_coverage || 0))} model estimate
        </span>
      </div>
      {leagues.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground text-sm" data-testid="all-live-empty">
            No games are live right now — check back when matches kick off.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {leagues.map((L) => {
            const key = `${L.league_id || L.league_name}`;
            const isCollapsed = collapsed[key];
            return (
              <Card key={key} className="overflow-hidden" data-testid={`all-live-league-${key}`}>
                <button
                  type="button"
                  onClick={() => setCollapsed((c) => ({ ...c, [key]: !c[key] }))}
                  className="w-full flex items-center justify-between px-3 py-2 bg-muted/40 hover:bg-muted/70 transition-colors"
                  data-testid={`all-live-league-toggle-${key}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {L.flag && <img src={L.flag} alt="" className="w-4 h-3 object-cover rounded-[2px]" />}
                    <span className="text-xs font-bold truncate">{L.league_name}</span>
                    {L.country && <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{L.country}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] tabular-nums text-muted-foreground">{L.matches.length}</span>
                    <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
                  </div>
                </button>
                {!isCollapsed && (
                  <div>{L.matches.map((m) => <AllLiveRow key={m.fixture_id} m={m} />)}</div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </section>
  );
};

// ───────────────────────────────────────────────────────────────────────
// List view
// ───────────────────────────────────────────────────────────────────────

const ListView = () => {
  const [matches, setMatches] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [allLive, setAllLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const [a, b, c] = await Promise.all([
        axios.get(`${API}/live/next-goal/matches`),
        axios.get(`${API}/live/next-goal/alerts/active`),
        axios.get(`${API}/live/next-goal/all-live`),
      ]);
      setMatches(a.data?.matches || []);
      setAlerts(b.data?.alerts || []);
      setAllLive(c.data || null);
    } catch {
      /* keep last data on transient errors */
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      await load();
    };
    tick();
    pollRef.current = setInterval(tick, 10000);
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/live/next-goal/admin/poll-now`);
    } catch {
      /* admin endpoint is best-effort */
    }
    await load();
  };

  return (
    <div className="space-y-6" data-testid="live-ai-list">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-rose-500 font-bold flex items-center gap-2">
            <span className="relative flex w-1.5 h-1.5">
              <span className="absolute inline-flex w-full h-full rounded-full bg-rose-500 opacity-75 animate-ping" />
              <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-rose-500" />
            </span>
            Live AI · Next Goal Engine
          </div>
          <h1 className="text-2xl sm:text-3xl font-black mt-1">Who scores the next goal?</h1>
          <p className="text-sm text-muted-foreground max-w-xl mt-1">
            Real-time momentum, pressure, xG and ensemble probabilities — refreshed every 15-30s by the adaptive engine.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing} data-testid="refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </header>

      {alerts.length > 0 && (
        <Card className="border-rose-500/40 bg-rose-500/5">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2 text-rose-500 font-bold uppercase tracking-[0.18em] text-xs">
              <BellRing className="w-4 h-4" /> Live alerts ({alerts.length})
            </div>
            <ul className="space-y-1.5 text-xs">
              {alerts.slice(0, 6).map((a, i) => (
                <li key={`${a.fixture_id}-${i}`} className="flex items-center gap-2" data-testid={`alert-${a.fixture_id}`}>
                  <span className="text-rose-500 font-bold tabular-nums">{a.probability?.toFixed?.(0)}%</span>
                  <span className="font-semibold">{a.label}</span>
                  <span className="text-muted-foreground">· {a.home_team} vs {a.away_team} · {a.minute}&apos;</span>
                  <Link to={`/live-ai/${a.fixture_id}`} className="text-rose-500 hover:underline ml-auto">Open →</Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Iter 204g — Gold Finland exact-score panel */}
      <GoldFinlandPanel />

      {/* Iter 238 — Gold Sweden + Gold Norway panels (same engine) */}
      <GoldFinlandPanel country="sweden" />
      <GoldFinlandPanel country="norway" />

      {loading ? (
        <div className="text-center text-muted-foreground py-12">
          <Activity className="w-10 h-10 mx-auto mb-3 animate-pulse opacity-40" />
          <p>Loading live AI engine…</p>
        </div>
      ) : matches.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center text-muted-foreground" data-testid="empty-state">
            <Flame className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="font-semibold text-foreground mb-1">No live AI matches right now</p>
            <p className="text-sm max-w-md mx-auto">
              The engine only surfaces matches where API-Sports is streaming real
              live stats (dangerous attacks, shots on target, xG, possession).
              Lower-tier leagues without stat coverage are skipped so you never see
              a fake 50/50 prediction.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {matches.map((m) => <MatchCard key={m.fixture_id} match={m} />)}
        </div>
      )}

      {/* Iter 275 — every live game worldwide, grouped by league */}
      {!loading && <AllLiveByLeague data={allLive} />}
    </div>
  );
};

// ───────────────────────────────────────────────────────────────────────
// Page entry
// ───────────────────────────────────────────────────────────────────────

const LiveNextGoalPage = () => {
  const { fixtureId } = useParams();
  return (
    <div className="min-h-screen bg-background">
      <TopNav />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {fixtureId ? <DetailView fixtureId={fixtureId} /> : <ListView />}
      </main>
    </div>
  );
};

export default LiveNextGoalPage;
